import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios'
import { useAuthStore } from './auth'
import router from '@/router'
import logger from '@/utils/logger'

interface AxiosRequestConfigWithRetry extends InternalAxiosRequestConfig {
  _csrfRetry?: boolean
}

// CSRF token storage
let csrfToken: string | null = null

// Fetch CSRF token from server
const fetchCsrfToken = async (): Promise<string | null> => {
  try {
    const response = await axios.get('/api/csrf-token', { withCredentials: true })
    csrfToken = response.data.csrfToken
    return csrfToken
  } catch (error) {
    logger.error('Failed to fetch CSRF token', error)
    return null
  }
}

// Initialize CSRF token on module load
fetchCsrfToken()

// Create axios instance
//
// Timeout note: 120s ceiling accommodates local AI cold-start latency on the
// HQ chat_copilot path. Local Gemma 4 26B-A4B on Asgard CPU has been observed
// at ~84s end-to-end on a fresh-conversation tool-using query (~20K input
// tokens for system prompt + 30+ tool schemas + multi-turn ReAct). Warm-cache
// queries land in ~5-15s. Cloud-provider inference (OpenAI/Claude/Gemini) is
// well under 10s and unaffected. Bumping above 120s starts trading user-
// perceived "AI is broken" for "AI is still trying" — 120s is the budget that
// holds for both extremes.
const api: AxiosInstance = axios.create({
  baseURL: '/api',
  timeout: 120000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
})

// Request interceptor - add CSRF token for state-changing requests
api.interceptors.request.use(
  async (config) => {
    // Only add CSRF token for state-changing methods
    const methodsRequiringCsrf = ['post', 'put', 'delete', 'patch']
    
    if (methodsRequiringCsrf.includes(config.method?.toLowerCase() || '')) {
      // If we don't have a token yet, fetch one
      if (!csrfToken) {
        await fetchCsrfToken()
      }
      
      if (csrfToken) {
        config.headers['x-csrf-token'] = csrfToken
      }
    }
    
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfigWithRetry
    
    // Handle CSRF token expiration - retry once with fresh token
    if (
      error.response?.status === 403 &&
      (error.response?.data as { error?: string })?.error?.includes('CSRF') &&
      !originalRequest._csrfRetry
    ) {
      originalRequest._csrfRetry = true
      
      // Refresh the CSRF token
      await fetchCsrfToken()
      
      // Update the header and retry
      if (csrfToken && originalRequest) {
        originalRequest.headers['x-csrf-token'] = csrfToken
        return api(originalRequest)
      }
    }
    
    // Handle session expiration - skip redirect for auth-check requests and public routes
    if (error.response?.status === 401) {
      const requestUrl = originalRequest?.url || ''
      const isAuthCheck = requestUrl === '/user' || requestUrl.endsWith('/api/user')
      const publicPaths = ['/login', '/bglogin', '/staff-login', '/extension', '/eula', '/privacy-policy', '/survey', '/burn']
      const isPublicRoute = publicPaths.some(p => window.location.pathname.startsWith(p))

      if (!isAuthCheck && !isPublicRoute) {
        const authStore = useAuthStore()
        authStore.logout()
        router.push('/login')
      }
    }
    
    return Promise.reject(error)
  }
)

export default api

// Export function to manually refresh token if needed
export const refreshCsrfToken = fetchCsrfToken

// Helper functions for common API patterns
export const apiGet = async <T>(url: string, params?: object): Promise<T> => {
  const response = await api.get<T>(url, { params })
  return response.data
}

export const apiPost = async <T>(url: string, data?: object): Promise<T> => {
  const response = await api.post<T>(url, data)
  return response.data
}

export const apiPut = async <T>(url: string, data?: object): Promise<T> => {
  const response = await api.put<T>(url, data)
  return response.data
}

export const apiDelete = async <T>(url: string): Promise<T> => {
  const response = await api.delete<T>(url)
  return response.data
}

export const apiUpload = async <T>(url: string, formData: FormData): Promise<T> => {
  const response = await api.post<T>(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
  return response.data
}
